/**
 * Tenant-flow step 1 — verify the firm owner's email via a 6-digit OTP.
 * No email gateway is wired for v1; the code is printed in the backend
 * log. The dev-mode hint informs the user where to find it.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";

export default function EmailVerifyStep({ state, onAdvance }) {
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [code, setCode] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [devNote, setDevNote] = useState(null);

    const sendOtp = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/onboarding/send-otp");
            setSent(true);
            setDevNote(data?.dev_note || null);
            toast.success(`OTP sent to ${data?.sent_to}`);
        } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
    };

    const verify = async () => {
        if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code"); return; }
        setVerifying(true);
        try {
            await api.post("/onboarding/verify-otp", { code });
            toast.success("Email verified");
            onAdvance?.();
        } catch (e) { toast.error(apiError(e)); } finally { setVerifying(false); }
    };

    return (
        <div className="max-w-md mx-auto space-y-5 text-center">
            <Mail className="w-12 h-12 text-primary mx-auto" strokeWidth={1.25}/>
            <div>
                <h2 className="text-2xl font-semibold tracking-tight text-foreground">Verify your email</h2>
                <p className="text-muted-foreground text-sm mt-2">
                    We&apos;ll send a 6-digit code to <strong className="text-foreground">{state.details?.email}</strong> to confirm it&apos;s yours.
                </p>
            </div>

            {!sent ? (
                <button onClick={sendOtp} disabled={sending} data-testid="onboarding-send-otp"
                    className="bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 px-6 py-2.5 text-xs uppercase tracking-[0.16em] font-medium inline-flex items-center gap-2">
                    <Send className="w-3.5 h-3.5" strokeWidth={1.5}/>
                    {sending ? "Sending…" : "Send verification code"}
                </button>
            ) : (
                <div className="space-y-3">
                    <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000" maxLength={6}
                        data-testid="onboarding-otp-input"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-4 py-3 text-2xl tracking-[0.4em] text-center font-mono"/>
                    <button onClick={verify} disabled={verifying || code.length !== 6} data-testid="onboarding-verify-otp"
                        className="w-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 px-6 py-2.5 text-xs uppercase tracking-[0.16em] font-medium">
                        {verifying ? "Verifying…" : "Verify code"}
                    </button>
                    <button onClick={sendOtp} disabled={sending} data-testid="onboarding-resend-otp"
                        className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
                        Resend code
                    </button>
                </div>
            )}

            {devNote && (
                <div className="mt-4 border border-amber-400/30 bg-amber-400/5 p-3 text-[10px] text-amber-200/80 text-left" data-testid="onboarding-dev-note">
                    <strong>Dev mode:</strong> {devNote} Look for <code className="bg-background px-1">ONBOARDING_OTP</code> in <code className="bg-background px-1">/var/log/supervisor/backend.err.log</code>.
                </div>
            )}
        </div>
    );
}
